// Lakai — homepage grid (2026-07-03 rebrand).
// Data source: <script id="p7-product-data"> emitted by sections/p7-product-grid.liquid
// Cards link to real /products/* URLs (hash-PDP retired); homepage model pills
// are real collection links as a no-JS fallback, then this file intercepts them
// for smooth in-page filtering. Cart drawer lives in p7-cart.js (site-wide,
// loaded by p7-header.liquid).

const DEFAULT_PRODUCT_WINDOW = 16;
const FILTER_ANIM_MS = 170;
// Half the thumb's travel: the grid is already fading back in by the time the
// thumb finishes sliding, so the two read as one gesture rather than a queue.
const GRID_FADE_MS = 85;

function reducedMotion() {
  return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Stock tiers. The Liquid computes `tier` per product against the best-stocked
// size using the thresholds in the section settings; these are the labels for
// it. Fallback below keeps old cached payloads (which carry no `tier`) from
// rendering an empty label.
const STOCK_LABEL = { high: 'In stock', mid: 'Low stock', low: 'Almost gone', out: 'Out of stock' };

function stockTier(p) {
  if (p.tier && STOCK_LABEL[p.tier]) return p.tier;
  const best = (p.variants || []).reduce((m, v) => Math.max(m, v.inventory || 0), 0);
  if (best <= 0) return 'out';
  if (best <= 5) return 'low';
  if (best <= 12) return 'mid';
  return 'high';
}

function positiveInt(value, fallback) {
  const n = parseInt(value, 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

const state = {
  products: [],
  shoeSizes: [],
  apparelSizes: [],
  filter: 'all',
  selectedShoeSizes: [],
  selectedApparelSizes: [],
  initialRenderCount: DEFAULT_PRODUCT_WINDOW,
  loadMoreCount: DEFAULT_PRODUCT_WINDOW,
  renderLimit: DEFAULT_PRODUCT_WINDOW,
  lastVisibleCount: 0,
  cart: { items: [], item_count: 0, total_price: 0 },
};

function configureProductWindow() {
  const grid = document.getElementById('grid');
  const config = state.config || {};
  const configInitial = positiveInt(config.initial_count, DEFAULT_PRODUCT_WINDOW);
  const configLoad = positiveInt(config.load_count, configInitial);
  state.initialRenderCount = positiveInt(grid && grid.dataset.initialCount, configInitial);
  state.loadMoreCount = positiveInt(grid && grid.dataset.loadCount, configLoad);
  resetProductWindow();
}

function resetProductWindow() {
  state.renderLimit = state.initialRenderCount || DEFAULT_PRODUCT_WINDOW;
}

function formatPrice(value) {
  const amount = Number(value) || 0;
  return '$' + Math.round(amount);
}

// Size vocabulary is owned by p7-size-prefs.js (defer, in <head>, so it has
// almost always run by now). Three copies of sizeCompare used to exist; see
// the comment on its definition there. Guarded (not a bare property read):
// p7-card-filter.js bails outright when P7SizePrefs is missing (`if (!P)
// return;`), but this file has no such early exit, so an unguarded call would
// throw inside init()'s try/catch and blank the whole homepage grid behind
// "Could not load products." Falling back to 0 (no reordering) instead lets
// the grid render — degraded, not dead.
const sizeCompare = (a, b) => window.P7SizePrefs ? window.P7SizePrefs.sizeCompare(a, b) : 0;

function applySizePrefs(prefs) {
  if (!prefs || typeof prefs !== 'object') return;
  const onlyKnown = (arr, known) => {
    if (!Array.isArray(arr)) return [];
    const set = known.map(s => String(s).trim().toUpperCase());
    return arr.filter(s =>
      typeof s === 'string' && set.includes(s.trim().toUpperCase()));
  };
  // onlyKnown just filters prefs down to sizes this page's payload actually
  // carries (state.shoeSizes / state.apparelSizes) — it is not where the
  // shoe-size cap lives. Shoe sizes are single-select, enforced upstream in
  // p7-size-prefs.normalizePrefs (the `.slice(0, 1)` on `s`), so it is
  // deliberately not repeated here; whatever normalizePrefs hands back is
  // already at most one shoe size.
  state.selectedShoeSizes = onlyKnown(prefs.s, state.shoeSizes);
  state.selectedApparelSizes = onlyKnown(prefs.a, state.apparelSizes);
}

function hydrateSizePrefs() {
  applySizePrefs(window.P7SizePrefs ? window.P7SizePrefs.read() : { s: [], a: [] });
}

function commitSizePrefs() {
  if (!window.P7SizePrefs) return;
  window.P7SizePrefs.set({
    s: state.selectedShoeSizes,
    a: state.selectedApparelSizes,
  });
}

// Invoked at the END of the file — the first render() runs cardHTML, which
// reads consts (TIER_CHIP, CARD_CARET) declared further down. Calling init
// here put those reads inside the temporal dead zone: a silent async
// ReferenceError that left the skeleton cards up until the first filter
// event re-rendered. Declaration here, invocation last.
async function init() {
  try {
    const dataEl = document.getElementById('p7-product-data');
    const data = JSON.parse(dataEl.textContent);
    state.products = data.products;
    state.shoeSizes = (data.shoe_sizes || []).slice().sort(sizeCompare);
    state.apparelSizes = (data.apparel_sizes || []).slice().sort(sizeCompare);
    state.config = data.config || {};
    // Grid order is merchant-controlled from Theme editor > Product grid.
    // Empty lists mean "no opinion", which collapses the rank comparators to 0
    // and leaves collection order intact — same as switching the sort off.
    const priorityList = (s) => String(s || '')
      .split(',').map((x) => x.trim().toLowerCase()).filter(Boolean);
    const sortOn = state.config.sort_enabled !== false;
    state.bucketPriority = sortOn ? priorityList(state.config.bucket_priority) : [];
    state.modelPriority = sortOn ? priorityList(state.config.model_priority) : [];
    state.newestFirst = sortOn;
    configureProductWindow();
  } catch (err) {
    document.getElementById('grid').textContent = 'Could not load products. Refresh the page.';
    console.error(err);
    return;
  }
  hydrateSizePrefs();
  recoverEmptyBootFilter();
  wireFilters();
  wireGlobalSizePrefs();
  wireCardSizeChips();
  wireLoadMore();
  render();
  renderActiveFilters();
}

function recoverEmptyBootFilter() {
  const hadSizeFilter = state.selectedShoeSizes.length > 0 || state.selectedApparelSizes.length > 0;
  if (!hadSizeFilter || visibleProducts().length > 0) return;
  state.selectedShoeSizes = [];
  state.selectedApparelSizes = [];
  commitSizePrefs();
}

function visibleProducts() {
  const hasShoeSizeFilter = state.selectedShoeSizes.length > 0;
  const hasApparelSizeFilter = state.selectedApparelSizes.length > 0;
  const list = state.products.filter(p => {
    if (!(p.variants || []).some(v => (v.inventory || 0) > 0)) return false;
    if (p.kids) return false;
    if (state.filter === 'shoes' && p.type !== 'shoe') return false;
    if (state.filter === 'apparel' && p.type !== 'apparel') return false;
    if (state.filter && state.filter.indexOf('model:') === 0) {
      if (p.type !== 'shoe') return false;
      if (!matchesModelFilter(p, state.filter.slice(6))) return false;
    }

    // OR across the selected sizes: a product qualifies if it stocks ANY of
    // them. Case-insensitive, matching the apparel branch below — the shoe
    // branch used to compare raw strings, so a payload size of "9.5 " or a
    // differently-cased letter size silently matched nothing.
    if (hasShoeSizeFilter && p.type === 'shoe') {
      const want = state.selectedShoeSizes.map(s => String(s).trim().toUpperCase());
      const hit = p.variants.some(v => want.includes(String(v.size || '').trim().toUpperCase()) && v.inventory > 0);
      if (!hit) return false;
    }
    if (hasApparelSizeFilter && p.type === 'apparel') {
      const want = state.selectedApparelSizes.map(s => s.toUpperCase());
      const hit = p.variants.some(v => want.includes((v.size || '').toUpperCase()) && v.inventory > 0);
      if (!hit) return false;
    }
    return true;
  });
  // Shoes before apparel, then bucket, then newest drop, then model. Bucket
  // and model come from the priority lists in Theme editor > Product grid,
  // and newness from each product's created date. Array.sort is stable
  // (ES2019+) so equal-rank items keep collection order, and `list` is a fresh
  // array from .filter() so state.products is untouched.
  const rank = (order, value) => {
    if (!order.length) return 0;
    const i = order.indexOf(String(value || '').toLowerCase());
    return i === -1 ? order.length : i; // unlisted sorts after everything listed
  };
  // Newest drop first, ranked ABOVE model so a new release leads the grid
  // instead of sitting behind its model's back catalogue. `created` is a
  // YYYY-MM-DD string, so a plain string compare is a date compare. Products
  // with no `created` sort last rather than jumping the queue.
  const newest = (a, b) => (state.newestFirst
    ? String(b.created || '').localeCompare(String(a.created || ''))
    : 0);
  return list.sort((a, b) =>
    (a.type === 'shoe' ? 0 : 1) - (b.type === 'shoe' ? 0 : 1)
    || rank(state.bucketPriority, a.bucket) - rank(state.bucketPriority, b.bucket)
    || newest(a, b)
    || rank(state.modelPriority, a.model) - rank(state.modelPriority, b.model));
}

function normalizeModel(value) {
  return String(value || '').trim().toLowerCase();
}

function displayCardName(value) {
  return String(value || '')
    .replace(/\s+SALE\b/g, ' Sale')
    .replace(/\s+NEW\b/g, ' New')
    .replace(/\s+FINAL\b/g, ' Final');
}

function matchesModelFilter(product, filterModel) {
  const want = normalizeModel(filterModel);
  if (!want) return true;
  const model = normalizeModel(product.model);
  const handle = normalizeModel(product.handle);
  const title = normalizeModel(product.title);
  return model === want ||
    model.indexOf(want + '-') === 0 ||
    handle.indexOf(want) >= 0 ||
    title.indexOf(want) >= 0;
}

function render() {
  const grid = document.getElementById('grid');
  const list = visibleProducts();
  const shown = list.slice(0, state.renderLimit);
  state.lastVisibleCount = list.length;
  document.getElementById('grid-empty').hidden = list.length > 0;
  grid.innerHTML = shown.map(cardHTML).join('');
  renderLoadMore(list.length, shown.length);
}

// Cross-fade the grid across a filter change.
//
// A FADE, not a skeleton, and the distinction is deliberate: this re-render is
// synchronous — the payload is already in memory — so a skeleton would be
// signalling a wait that does not exist, and the delay needed to show one would
// have to be invented. The PDP tier tabs DO use a skeleton (p7-pdp-tier.js /
// .p7-pdp-tier-boot) because jumping tiers is a real page load. Same house
// style, different signal, because they are different waits.
//
// Only the model pills and Clear all route through here. Load-more must not:
// it appends to the existing set, and fading the whole grid to re-show cards
// that never left would be a lie about what changed.
let gridSwapTimer = null;

function renderSwap() {
  const grid = document.getElementById('grid');
  if (!grid || reducedMotion()) {
    render();
    renderActiveFilters();
    return;
  }
  // Rapid taps: drop the in-flight swap rather than stacking timers, or the
  // second tap's fade-in races the first one's class removal and the grid
  // sticks at opacity 0.
  if (gridSwapTimer) window.clearTimeout(gridSwapTimer);
  grid.classList.add('is-swapping');
  gridSwapTimer = window.setTimeout(() => {
    gridSwapTimer = null;
    render();
    renderActiveFilters();
    // The new cards have to paint at opacity 0 before the class comes off.
    // Removing it in the same frame as the innerHTML write lets the browser
    // coalesce both into one style recalculation, and nothing animates.
    window.requestAnimationFrame(() => grid.classList.remove('is-swapping'));
  }, GRID_FADE_MS);
}

function renderLoadMore(total, shown) {
  const wrap = document.getElementById('grid-more');
  const button = document.getElementById('grid-load-more');
  const count = document.getElementById('grid-count');
  const grid = document.getElementById('grid');
  if (!wrap || !button) return;

  const hasMore = shown < total;
  if (grid) grid.classList.toggle('has-more', hasMore);
  wrap.hidden = !hasMore;
  button.hidden = !hasMore;
  button.disabled = !hasMore;
  if (count) count.textContent = hasMore ? 'Showing ' + shown + ' of ' + total : '';
}

function wireLoadMore() {
  const button = document.getElementById('grid-load-more');
  if (!button) return;
  button.addEventListener('click', () => {
    state.renderLimit = Math.min(
      state.renderLimit + state.loadMoreCount,
      state.lastVisibleCount || state.renderLimit + state.loadMoreCount
    );
    render();
  });
}

// ---------------------------------------------------------------------------
// Unified card (design handoff 2026-08-04). This mirrors the Liquid card in
// snippets/p7-card.liquid — keep the two markups in step. The panel
// open/close behaviour is p7-card.js (delegated, survives re-renders).
// ---------------------------------------------------------------------------

// Tier chips are bucket-derived, matching p7-card.liquid and the PDP sibling
// tabs. Price only splits closeout buckets into Sale vs Final.
function tierLabel(p) {
  const bucket = String(p.bucket || '').trim().toLowerCase();
  const cents = Math.round((Number(p.sale_price) || 0) * 100);
  const finalMax = 4900;
  if (bucket === 'new') return 'New';
  if (bucket === 'evergreen' || bucket === 'sale') {
    return cents <= finalMax ? 'Final' : 'Sale';
  }
  if (bucket === 'last-season' || bucket === 'final') {
    return cents <= finalMax ? 'Final' : 'Sale';
  }
  return '';
}

function toggleSizeValue(list, value) {
  const want = String(value || '').trim().toUpperCase();
  if (!want) return Array.isArray(list) ? list.slice() : [];
  const current = Array.isArray(list) ? list.slice() : [];
  const has = current.some(s => String(s).trim().toUpperCase() === want);
  return has
    ? current.filter(s => String(s).trim().toUpperCase() !== want)
    : current.concat([value]);
}

const CARD_CARET =
  '<svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true" focusable="false">' +
  '<path d="M2 4l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.5" ' +
  'stroke-linecap="round" stroke-linejoin="round"/></svg>';

// Elite badge markup comes from the <template> the section renders, so the
// star glyph is defined once (p7-elite-mark.liquid) and cannot drift.
let eliteBadgeHTML = null;
function eliteHTML() {
  if (eliteBadgeHTML === null) {
    const tpl = document.getElementById('p7-card-elite-tpl');
    eliteBadgeHTML = tpl ? tpl.innerHTML.trim() : '';
  }
  return eliteBadgeHTML;
}
let proBadgeHTML = null;
function proHTML() {
  if (proBadgeHTML === null) {
    const tpl = document.getElementById('p7-card-pro-tpl');
    proBadgeHTML = tpl ? tpl.innerHTML.trim() : '';
  }
  return proBadgeHTML;
}

let newBadgeHTML = null;
function newHTML() {
  if (newBadgeHTML === null) {
    const tpl = document.getElementById('p7-card-new-tpl');
    newBadgeHTML = tpl ? tpl.innerHTML.trim() : '';
  }
  return newBadgeHTML;
}

// Unique real sizes in variant order, each with an in-stock flag. The Liquid
// already nulls 'Default Title'/'OS' pseudo-sizes into size: null.
function cardSizeRun(p) {
  const seen = {};
  const sizes = [];
  (p.variants || []).forEach(v => {
    if (!v.size) return;
    const key = String(v.size).trim().toUpperCase();
    if (!seen[key]) {
      seen[key] = { size: v.size, available: false };
      sizes.push(seen[key]);
    }
    if ((v.inventory || 0) > 0) seen[key].available = true;
  });
  return sizes;
}

function cardHTML(p) {
  const url = p.url || ('/products/' + encodeURIComponent(p.handle));
  const parts = String(p.title || '').split(' - ');
  const name = displayCardName(parts[0] || String(p.title || ''));
  const colour = parts.length > 1 ? parts[parts.length - 1] : '';
  const tierChip = tierLabel(p);
  const hasCompare = p.original_price && p.original_price > p.sale_price;
  const priceHTML = hasCompare
    ? '<s>' + formatPrice(p.original_price) + '</s><b class="is-sale">' + formatPrice(p.sale_price) + '</b>'
    : '<b>' + formatPrice(p.sale_price) + '</b>';

  const sizes = cardSizeRun(p);
  const availCount = sizes.filter(s => s.available).length;

  // Stock chip. Default tier is the Liquid-computed max-based tier
  // (stockTier); with a size filter active the card shows the handoff's
  // 'active' state instead — visibleProducts() already narrowed the grid,
  // so a visible card almost always stocks the size.
  let level = stockTier(p);
  let label = STOCK_LABEL[level];
  const selShoe = state.selectedShoeSizes;
  const selApp = state.selectedApparelSizes;
  const isShoe = p.type === 'shoe';
  let activeSize = '';
    if (isShoe && !p.kids && selShoe.length) {
      const mine = sizes.filter(s => s.available).map(s => String(s.size).toUpperCase());
      activeSize = selShoe.find(s => mine.indexOf(String(s).toUpperCase()) !== -1) || selShoe[0];
  } else if (!isShoe && selApp.length) {
    const mine = sizes.filter(s => s.available).map(s => String(s.size).toUpperCase());
    activeSize = selApp.find(s => mine.indexOf(String(s).toUpperCase()) !== -1) || selApp[0];
  }
  if (activeSize) {
    const has = sizes.some(s =>
      String(s.size).toUpperCase() === String(activeSize).toUpperCase() && s.available);
    level = has ? 'active' : 'low';
    label = has ? 'Size ' + activeSize : 'Not in ' + activeSize;
  }

  // Every size the shopper picked that THIS card actually stocks, in the card's
  // own size order rather than selection order, so two cards never disagree
  // about which comes first.
  const selected = isShoe ? (p.kids ? [] : selShoe) : selApp;
  const matched = selected.length
    ? sizes.filter(s => s.available &&
        selected.some(sel => String(sel).trim().toUpperCase() === String(s.size).trim().toUpperCase()))
        .map(s => s.size)
    : [];

  // One pill per matched size; the first carries the dot so the row still reads
  // as a stock indicator rather than a bare tag list. data-stock-level and
  // data-stock-label are emitted so a later in-place update can restore the
  // original tier — the Liquid card at p7-card.liquid already carries them and
  // this one did not, which meant the two cards were not interchangeable.
  const stockChip = matched.length
    ? '<span class="card__stock card__stock--active card__stock--pills" data-stock' +
        ' data-stock-level="' + escapeHtml(stockTier(p)) + '"' +
        ' data-stock-label="' + escapeHtml(STOCK_LABEL[stockTier(p)]) + '">' +
        matched.map((s, i) =>
          '<span class="card__size-pill">' +
            (i === 0 ? '<i class="card__dot" aria-hidden="true"></i>' : '') +
            escapeHtml(String(s)) +
          '</span>').join('') +
      '</span>'
    : '<span class="card__stock card__stock--' + level + '" data-stock' +
        ' data-stock-level="' + escapeHtml(stockTier(p)) + '"' +
        ' data-stock-label="' + escapeHtml(STOCK_LABEL[stockTier(p)]) + '">' +
        '<i class="card__dot" aria-hidden="true"></i>' +
        '<span data-stock-text>' + escapeHtml(label) + '</span>' +
      '</span>';

  // Size chips deep-link into the filterable collection with the size
  // preselected (?size= is read by p7-clx-filter.js on load). Every size is
  // selectable, stocked or not — is-out only changes the look.
  const chipBase = isShoe ? '/collections/shoes?size=' : '/collections/apparel?size=';
  const runHTML = sizes.map(s => {
    const sizeUpper = String(s.size).toUpperCase();
    const current = activeSize && sizeUpper === String(activeSize).toUpperCase();
    return '<a class="card__chip' + (s.available ? '' : ' is-out') + '"' +
      (current ? ' aria-current="true"' : '') +
      ' href="' + chipBase + encodeURIComponent(s.size) + '"' +
      ' data-size-chip data-size="' + escapeHtml(s.size) + '">' +
      escapeHtml(s.size) + '</a>';
  }).join('');

  const stockRow = sizes.length
    ? '<div class="card__stock-row">' +
        '<button type="button" class="card__toggle" aria-expanded="false" data-toggle>' +
          stockChip + CARD_CARET +
        '</button>' +
      '</div>' +
      '<div class="card__sizes" hidden data-panel>' +
        '<div class="card__sizes-head"><span>' +
          (isShoe ? 'US sizes' : 'Sizes') +
        '</span></div>' +
        '<div class="card__run">' + runHTML + '</div>' +
      '</div>'
    : '<div class="card__stock-row">' + stockChip + '</div>';

  return [
    // data-ptype / data-kids exist for the size-chip handler below: it has to
    // know which preference bucket a chip writes to, and kids cards are
    // excluded from the shoe-size filter entirely (see the selShoe guard).
    '<article class="card" data-card data-ptype="' + (isShoe ? 'shoe' : 'apparel') +
      '"' + (p.kids ? ' data-kids' : '') + '>',
      '<a class="card__frame" href="' + escapeHtml(url) + '">',
        '<img src="' + escapeHtml(p.image || '') + '" alt="' + escapeHtml(p.title) + '" loading="lazy" decoding="async" width="600" height="600">',
        (p.drop ? newHTML() : ''),
        (p.elite ? eliteHTML() : ''),
        (p.pro ? proHTML() : ''),
      '</a>',
      '<div class="card__body">',
        (tierChip ? '<p class="card__tags"><span class="card__tag">' + tierChip + '</span></p>' : ''),
        '<div class="card__head">',
          '<a class="card__title" href="' + escapeHtml(url) + '">' + escapeHtml(name) + '</a>',
          '<span class="card__price">' + priceHTML + '</span>',
        '</div>',
        (colour ? '<p class="card__colour">' + escapeHtml(colour) + '</p>' : ''),
        stockRow,
      '</div>',
    '</article>',
  ].join('');
}

function wireFilters() {
  const track = document.querySelector('[data-model-filter-track]');
  const thumb = track ? track.querySelector('[data-model-filter-thumb]') : null;
  const pills = track ? Array.from(track.querySelectorAll('[data-filter]')) : [];

  function placeThumb(pill, animate) {
    if (!track || !thumb || !pill) return;
    thumb.style.transition = animate && !reducedMotion()
      ? 'left ' + FILTER_ANIM_MS + 'ms cubic-bezier(.2,.8,.2,1), width ' + FILTER_ANIM_MS + 'ms cubic-bezier(.2,.8,.2,1)'
      : 'none';
    thumb.style.left = pill.offsetLeft + 'px';
    thumb.style.width = pill.offsetWidth + 'px';
    track.classList.add('has-thumb');
  }

  function syncModelPills(animate) {
    let active = null;
    pills.forEach(btn => {
      const isActive = btn.dataset.filter === state.filter;
      btn.classList.toggle('active', isActive);
      if (isActive) {
        btn.setAttribute('aria-current', 'true');
        active = btn;
      } else {
        btn.removeAttribute('aria-current');
      }
    });
    placeThumb(active || pills[0], animate);
  }

  document.querySelectorAll('.pill[data-filter]').forEach(btn => {
    btn.addEventListener('click', (event) => {
      if (
        event.defaultPrevented ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        (event.button != null && event.button !== 0)
      ) {
        return;
      }
      event.preventDefault();
      state.filter = btn.dataset.filter;
      syncModelPills(true);
      resetProductWindow();
      renderSwap();
    });
  });
  syncModelPills(false);
  window.addEventListener('resize', () => syncModelPills(false));

  const clearBtn = document.getElementById('clear-filters');
  if (clearBtn) clearBtn.addEventListener('click', clearAllFilters);
}

function setCategory(value) {
  state.filter = value || 'all';
  const track = document.querySelector('[data-model-filter-track]');
  const thumb = track ? track.querySelector('[data-model-filter-thumb]') : null;
  const active = document.querySelector('.pill[data-filter="' + state.filter + '"]');
  document.querySelectorAll('.pill[data-filter]').forEach(b => {
    const isActive = b.dataset.filter === state.filter;
    b.classList.toggle('active', isActive);
    if (isActive) b.setAttribute('aria-current', 'true');
    else b.removeAttribute('aria-current');
  });
  if (track && thumb && active) {
    thumb.style.transition = reducedMotion()
      ? 'none'
      : 'left ' + FILTER_ANIM_MS + 'ms cubic-bezier(.2,.8,.2,1), width ' + FILTER_ANIM_MS + 'ms cubic-bezier(.2,.8,.2,1)';
    thumb.style.left = active.offsetLeft + 'px';
    thumb.style.width = active.offsetWidth + 'px';
  }
}

function clearAllFilters() {
  setCategory('all');
  state.selectedShoeSizes = [];
  state.selectedApparelSizes = [];
  commitSizePrefs();
  resetProductWindow();
  renderSwap();
}

function syncSizePrefs(event) {
  applySizePrefs(event && event.detail ? event.detail : (window.P7SizePrefs ? window.P7SizePrefs.read() : { s: [], a: [] }));
  resetProductWindow();
  render();
  renderActiveFilters();
}

function wireGlobalSizePrefs() {
  document.addEventListener('p7:size-prefs-change', syncSizePrefs);
  document.addEventListener('p7:size-prefs-ready', syncSizePrefs, { once: true });
}

/* Picking a size ON A CARD filters this grid in place (owner 2026-08-05).
   The chips render as links to /collections/<type>?size=, which meant every
   size tap left the page to re-render the same shoes one route away. The
   products are already here, so intercept and filter locally instead; the
   href stays as the no-JS fallback and as the behaviour for anything this
   handler declines below.

   Selection semantics match the drawer exactly, because both write the same
   P7SizePrefs store: shoe is single-select and re-picking the active size
   clears it; apparel is a multi-select toggle.

   Declining is deliberate rather than defensive. We only preventDefault when
   the size is one this grid actually knows (state.shoeSizes /
   state.apparelSizes) — applySizePrefs runs every stored value through
   onlyKnown(), so writing an unknown size would be silently dropped and the
   tap would do nothing at all. Kids cards are skipped for the same reason:
   render() excludes them from the shoe-size filter, so a kids chip would
   filter away the very card it was clicked on. Both cases fall through to
   the link, which is today's behaviour. */
function wireCardSizeChips() {
  document.addEventListener('click', function (e) {
    const chip = e.target.closest('[data-size-chip]');
    if (!chip) return;
    const card = chip.closest('[data-card]');
    if (!card) return;

    const size = String(chip.getAttribute('data-size') || '').trim();
    if (!size) return;

    const isShoe = card.getAttribute('data-ptype') === 'shoe';
    if (isShoe && card.hasAttribute('data-kids')) return;

    const known = isShoe ? state.shoeSizes : state.apparelSizes;
    const match = (known || []).find(
      (s) => String(s).trim().toUpperCase() === size.toUpperCase());
    if (!match) return;

    e.preventDefault();

    if (isShoe) {
      state.selectedShoeSizes = window.P7SizePrefs
        ? window.P7SizePrefs.toggleShoe(match, { s: state.selectedShoeSizes, a: state.selectedApparelSizes }).s
        : toggleSizeValue(state.selectedShoeSizes, match);
    } else {
      const had = state.selectedApparelSizes.some(
        (s) => String(s).trim().toUpperCase() === match.toUpperCase());
      state.selectedApparelSizes = had
        ? state.selectedApparelSizes.filter(
            (s) => String(s).trim().toUpperCase() !== match.toUpperCase())
        : state.selectedApparelSizes.concat([match]);
    }

    // Immediate feedback WITHOUT a re-render. render() replaces grid.innerHTML,
    // which destroys the open panel — that is why the panel used to vanish the
    // instant you tapped a size, and why the fix belongs here rather than in
    // p7-card.js (which never closes the panel on a chip tap).
    const nowOn = (isShoe ? state.selectedShoeSizes : state.selectedApparelSizes).some(
      (s) => String(s).trim().toUpperCase() === match.toUpperCase());
    if (nowOn) chip.setAttribute('aria-current', 'true');
    else chip.removeAttribute('aria-current');

    // Prefs commit now so other components and pages stay in sync immediately;
    // only the grid rebuild waits.
    commitSizePrefs();
    scheduleSizeRender();
  });
}

// The grid rebuild after a size tap is debounced so quick multi-size picks
// rebuild once instead of tearing the card panel down between taps.
const SIZE_RENDER_DELAY = 0;
let sizeRenderTimer = null;
function scheduleSizeRender() {
  if (sizeRenderTimer) clearTimeout(sizeRenderTimer);
  sizeRenderTimer = setTimeout(() => {
    sizeRenderTimer = null;
    resetProductWindow();
    render();
    renderActiveFilters();
  }, SIZE_RENDER_DELAY);
}

// Active-filter chip rail, built with DOM APIs so all labels use textContent.
function renderActiveFilters() {
  const wrap = document.getElementById('active-filters');
  const toggle = document.getElementById('filter-toggle');
  const clearBtn = document.getElementById('clear-filters');
  if (!wrap) return;

  while (wrap.firstChild) wrap.removeChild(wrap.firstChild);

  state.selectedShoeSizes.forEach(size => {
    const sizeChip = document.createElement('button');
    sizeChip.type = 'button';
    sizeChip.className = 'filter-chip filter-chip--size';
    sizeChip.setAttribute('aria-label', 'Clear shoe size filter, size ' + size);
    const label = document.createElement('span');
    label.textContent = size;
    const x = document.createElement('span');
    x.className = 'filter-chip__x';
    x.setAttribute('aria-hidden', 'true');
    x.textContent = '✕';
    sizeChip.appendChild(label);
    sizeChip.appendChild(x);
    sizeChip.addEventListener('click', () => removeShoeSize(size));
    wrap.appendChild(sizeChip);
  });

  state.selectedApparelSizes.forEach(size => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'filter-chip filter-chip--size';
    btn.setAttribute('aria-label', 'Clear apparel size filter, size ' + size);
    const label = document.createElement('span');
    label.textContent = size;
    const x = document.createElement('span');
    x.className = 'filter-chip__x';
    x.setAttribute('aria-hidden', 'true');
    x.textContent = '✕';
    btn.appendChild(label);
    btn.appendChild(x);
    btn.addEventListener('click', () => {
      state.selectedApparelSizes = state.selectedApparelSizes.filter(s => s !== size);
      commitSizePrefs();
      resetProductWindow();
      render();
      renderActiveFilters();
    });
    wrap.appendChild(btn);
  });

  const activeCount = state.selectedShoeSizes.length + state.selectedApparelSizes.length;
  if (toggle) toggle.classList.toggle('has-value', activeCount > 0);
  if (clearBtn) clearBtn.hidden = activeCount === 0;
}

function clearShoeSizeFilter() {
  state.selectedShoeSizes = [];
  commitSizePrefs();
  resetProductWindow();
  render();
  renderActiveFilters();
}

// Drop ONE shoe size, leaving the rest. clearShoeSizeFilter wipes all of them,
// which was the only behaviour needed while the selection was capped at one.
function removeShoeSize(size) {
  const want = String(size || '').trim().toUpperCase();
  state.selectedShoeSizes = state.selectedShoeSizes.filter(
    (s) => String(s).trim().toUpperCase() !== want);
  commitSizePrefs();
  resetProductWindow();
  render();
  renderActiveFilters();
}

// Build a size grid via DOM APIs (no innerHTML). Null-safe: the drawer
// section may omit a group via its theme-editor toggles.
function buildSizeGrid(grid, sizes, dataKey, singleSelect) {
  if (!grid) return;
  while (grid.firstChild) grid.removeChild(grid.firstChild);
  sizes.forEach(s => {
    const b = document.createElement('button');
    b.type = 'button';
    b.dataset[dataKey] = s;
    b.textContent = s;
    // Shoes are single-select, apparel is multi. The shoe branch clears its
    // siblings first so the popup can never show two shoe sizes lit — Apply
    // reads `button.selected` straight out of the grid, so a grid that allowed
    // two would hand two to a state that caps at one, and the second would
    // vanish on commit with no explanation on screen.
    b.addEventListener('click', () => {
      if (singleSelect && !b.classList.contains('selected')) {
        grid.querySelectorAll('button.selected').forEach(other => {
          other.classList.remove('selected');
        });
      }
      b.classList.toggle('selected');
    });
    grid.appendChild(b);
  });
}

function wireSizeModal() {
  const modal = document.getElementById('size-modal');
  if (!modal) return;
  const backdrop = document.getElementById('size-modal-backdrop');
  const closeBtn = document.getElementById('size-modal-close');
  const skipBtn = document.getElementById('size-modal-skip');
  const applyBtn = document.getElementById('size-modal-apply');
  const shoeGrid = document.getElementById('size-grid-shoe');
  const apparelGrid = document.getElementById('size-grid-apparel');

  buildSizeGrid(shoeGrid, state.shoeSizes, 'shoeSize', true);
  buildSizeGrid(apparelGrid, state.apparelSizes, 'apparelSize', false);

  function close() {
    modal.hidden = true;
  }
  function browseAll() {
    state.selectedShoeSizes = [];
    state.selectedApparelSizes = [];
    commitSizePrefs();
    resetProductWindow();
    renderActiveFilters();
    render();
    close();
  }
  if (backdrop) backdrop.addEventListener('click', close);
  if (closeBtn) closeBtn.addEventListener('click', close);
  if (skipBtn) skipBtn.addEventListener('click', browseAll);
  if (applyBtn) applyBtn.addEventListener('click', () => {
    state.selectedShoeSizes = shoeGrid
      ? Array.from(shoeGrid.querySelectorAll('button.selected')).map(b => b.dataset.shoeSize)
      : [];
    state.selectedApparelSizes = apparelGrid
      ? Array.from(apparelGrid.querySelectorAll('button.selected')).map(b => b.dataset.apparelSize)
      : [];
    commitSizePrefs();
    resetProductWindow();
    renderActiveFilters();
    render();
    close();
  });
}

function openSizeModal() {
  const modal = document.getElementById('size-modal');
  if (!modal) return;
  const shoeGrid = document.getElementById('size-grid-shoe');
  const apparelGrid = document.getElementById('size-grid-apparel');
  if (shoeGrid) shoeGrid.querySelectorAll('button').forEach(b => {
    b.classList.toggle('selected', state.selectedShoeSizes.includes(b.dataset.shoeSize));
  });
  if (apparelGrid) apparelGrid.querySelectorAll('button').forEach(b => {
    b.classList.toggle('selected', state.selectedApparelSizes.includes(b.dataset.apparelSize));
  });
  modal.hidden = false;
}

function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// Last line on purpose — every top-level const above is initialized before the
// first render. See the note on init()'s declaration.
init();
